import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Grid3X3, Shield, CheckCircle, Package, Settings } from "lucide-react";
import { LevelAppsSidebar } from "./LevelAppsSidebar";
import { LevelAppsCategoriesContent } from "./LevelAppsCategoriesContent";
import { LevelAppsWhitelistContent } from "./LevelAppsWhitelistContent";
import { LevelAppsBlacklistContent } from "./LevelAppsBlacklistContent";
import { LevelAppsAllAppsContent } from "./LevelAppsAllAppsContent";
import { LevelAppsSettingsContent } from "./LevelAppsSettingsContent";
import { useLevelAppMode } from "@/hooks/useLevelAppMode";
import type { AppMode } from "@/types/convex";

const appsSidebarItems = [
  { id: "settings", label: "Settings", icon: Settings },
  { id: "categories", label: "App Categories", icon: Grid3X3 },
  { id: "whitelist", label: "Whitelist", icon: CheckCircle },
  { id: "blacklist", label: "Blacklist", icon: Shield },
  { id: "all-apps", label: "All Apps", icon: Package },
];

interface LevelAppsTabProps {
  levelId: string;
  appFilter?: any;
  allowedApps?: string[];
  blockedApps?: string[];
}

export const LevelAppsTab = ({ levelId, appFilter, allowedApps, blockedApps }: LevelAppsTabProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState("settings");

  // Use the new mode hook for explicit mode management
  const { appMode, setAppMode, isLoading } = useLevelAppMode(levelId);

  /**
   * Filter sidebar items based on explicit appMode:
   * - whitelist: Settings, Whitelist only
   * - blacklist: Settings, Blacklist only
   * - categories: All sections
   */
  const filteredSidebarItems = 
    appMode === "blacklist" 
      ? appsSidebarItems.filter(item => ["settings", "blacklist"].includes(item.id))
      : appMode === "whitelist"
      ? appsSidebarItems.filter(item => ["settings", "whitelist"].includes(item.id))
      : appsSidebarItems; // categories mode - show all

  // Restore active section from URL params
  useEffect(() => {
    const section = searchParams.get('appsSection');
    if (section && ['settings', 'categories', 'whitelist', 'blacklist', 'all-apps'].includes(section)) {
      setActiveSection(section);
    }
  }, [searchParams]);

  // Switch to appropriate section if current section is not available for the mode
  useEffect(() => {
    const availableIds = filteredSidebarItems.map(item => item.id);
    if (!availableIds.includes(activeSection)) {
      setActiveSection(filteredSidebarItems[0]?.id || "settings");
    }
  }, [appMode, activeSection, filteredSidebarItems]);

  // Save active section to URL params
  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('appsSection', section);
    setSearchParams(newSearchParams);
  };

  // Handle app mode change from settings - uses the new hook
  const handleAppModeChange = async (mode: AppMode) => {
    await setAppMode(mode);
  };

  const renderContent = () => {
    switch (activeSection) {
      case "settings":
        return (
          <LevelAppsSettingsContent
            levelId={levelId}
            appMode={appMode}
            onAppModeChange={handleAppModeChange}
          />
        );
      case "categories":
        return <LevelAppsCategoriesContent levelId={levelId} appFilter={appFilter} />;
      case "whitelist":
        return <LevelAppsWhitelistContent levelId={levelId} allowedApps={allowedApps} />;
      case "blacklist":
        return <LevelAppsBlacklistContent levelId={levelId} blockedApps={blockedApps} allowedApps={allowedApps} />;
      case "all-apps":
        return <LevelAppsAllAppsContent levelId={levelId} appFilter={appFilter} allowedApps={allowedApps} blockedApps={blockedApps} />;
      default:
        return (
          <LevelAppsSettingsContent
            levelId={levelId}
            appMode={appMode}
            onAppModeChange={handleAppModeChange}
          />
        );
    }
  };

  return (
    <div className="flex h-full">
      <LevelAppsSidebar
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        items={filteredSidebarItems}
      />
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};
